// shared-lib/vanity-assembly/attachVanityLinks.js
//
// Phase 1c glue: take a fully-assembled vanity fixture group, emit the
// build-time auto-links (`emitVanityLinks`), merge them with whatever links
// the cabinet anchor already carries (`mergeLinks`), and return a new objects
// array with the merged links stamped onto the cabinet's
// `groupCustomization.links`.
//
// Kept separate from `emitVanityLinks` (pure inspector) so the merge + anchor-
// stamping policy lives in one place that every build/optimize/apply call site
// can reuse without duplicating the "find cabinet, read existing links, write
// back" dance.

const { emitVanityLinks } = require('./emitVanityLinks');
const { getVanityCabinet } = require('./fixtureGroup');
const { mergeLinks, reconcileGroupLinks } = require('../group-customization');

/**
 * Attach freshly-emitted auto-links to a single vanity group's cabinet anchor.
 *
 * @param {Object[]} groupObjects — one fixture group's objects (cabinet +
 *   accessories), already in world space.
 * @param {Object} [parentWall] — forwarded to `emitVanityLinks` (currently
 *   unused there; threaded for forward-compatibility).
 * @returns {Object[]} a new array. The cabinet object is shallow-cloned with
 *   updated `groupCustomization.links`; all other objects pass through by
 *   reference. Returns the input unchanged when there is no cabinet, or when
 *   there is nothing to record and no existing customization to preserve.
 */
function attachVanityLinks(groupObjects, parentWall) {
  if (!Array.isArray(groupObjects) || groupObjects.length === 0) {
    return groupObjects;
  }
  const cabinet = getVanityCabinet(groupObjects);
  if (!cabinet) return groupObjects;

  const emitted = emitVanityLinks(groupObjects, parentWall);
  const existingCz = cabinet.groupCustomization || null;
  const existingLinks = existingCz?.links || [];
  const validIds = new Set(groupObjects.map((o) => o?.id).filter(Boolean));
  const merged = mergeLinks(existingLinks, emitted, { validIds });
  // Born verified: rewrite any never-verified offset from this geometry and
  // stamp the rest, moving nothing (layout-relationships §3.2).
  const { links } = reconcileGroupLinks(groupObjects, merged);

  // Nothing to record and nothing already stored → don't stamp an empty
  // groupCustomization onto e.g. a cabinet-only vanity.
  if (links.length === 0 && !existingCz) return groupObjects;

  const updatedCabinet = {
    ...cabinet,
    // The anchor stored fact: this module DECIDED the cabinet carries the
    // record — say so, so no reader re-derives the choice.
    groupAnchor: true,
    groupCustomization: {
      ...(existingCz || {}),
      links,
    },
  };
  return groupObjects.map((o) =>
    o && o.id === cabinet.id ? updatedCabinet : o
  );
}

/**
 * Attach auto-links for one or more vanity groups inside a full room objects
 * array. Each named group is sliced out, run through `attachVanityLinks`, and
 * merged back in place. Objects outside the named groups pass through
 * untouched.
 *
 * @param {Object[]} allObjects — every object in the room.
 * @param {Iterable<string>} fixtureGroupIds — groups to (re)emit links for.
 * @param {Object} [parentWall]
 * @returns {Object[]} a new array with the affected cabinets updated.
 */
function attachVanityLinksForGroups(allObjects, fixtureGroupIds, parentWall) {
  if (!Array.isArray(allObjects) || allObjects.length === 0) return allObjects;
  const groupIds = new Set([...(fixtureGroupIds || [])].filter(Boolean));
  if (groupIds.size === 0) return allObjects;

  // Map each affected group's id → its updated cabinet (by id) so we can
  // splice the result back without reordering the room.
  const replacements = new Map();
  for (const gid of groupIds) {
    const groupObjects = allObjects.filter((o) => o?.fixtureGroupId === gid);
    if (groupObjects.length === 0) continue;
    const updated = attachVanityLinks(groupObjects, parentWall);
    if (updated === groupObjects) continue; // no change for this group
    for (const obj of updated) {
      const original = groupObjects.find((o) => o.id === obj.id);
      if (original !== obj) replacements.set(obj.id, obj);
    }
  }
  if (replacements.size === 0) return allObjects;
  return allObjects.map((o) =>
    o && replacements.has(o.id) ? replacements.get(o.id) : o
  );
}

module.exports = { attachVanityLinks, attachVanityLinksForGroups };
